import lightFormatDate from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import subDays from "date-fns/subDays";
import groupBy from "lodash/groupBy";
import PropTypes from "prop-types";
import React, { useContext, useReducer, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";
import { Button, Divider, Grid, Icon, Message, Pagination, Popup, Segment, Table } from "semantic-ui-react";
import uuid from "uuid/v4";
import { Config } from "../../../config/api";
import { checkIfWmx, formatMoney, formatNumber } from "../../../libs/common_utils";
import NewToBrandReportModel from "../../../models/newToBrandReport";
import Pager from "../../../models/pager";
import ReportModel from "../../../models/report";
import AgenciesService from "../../../services/agencies";
import ReportsService from "../../../services/reports";
import { walmartMXTranslations } from "../../../translations/walmartMXTranslations";
import useCanAccessRetailAttribution from "../../common/hooks/useCanAccessRetailAttribution";
import ColumnPicker from "../common/column-picker";
import ReportGridContext from "../common/context";
import { reportActions, reportGridReducer } from "../common/reducers";
import AVAILABLE_COLUMNS from "../fixtures/columns";
import REPORT_TYPES from "../fixtures/report-types";
import NewToBrandReportForm from "./newToBrandReport/form";
import ReportParamsForm from "./standardReports/form";

const initialState = {
  items: null,
  allItems: null,
  pager: new Pager(Config.reportingRowsPerPage),
  sortedBy: null,
  sortDirection: null,
};

const DATE_NOW = new Date();
const INITIAL_START_DATE = subDays(DATE_NOW, 7);
const INITIAL_END_DATE = subDays(DATE_NOW, 1);

const isConversionColumnLabel = (label) =>
  typeof label === "string" && label.trim().toLowerCase().endsWith("conversions");

const isStrategyColumn = (columnId) => columnId === "strategy_id" || columnId === "strategy_name";

/**
 * Downloads blob as a file with a given name.
 * @param {Blob} blob
 * @param {string} filename
 */
function downloadBlob(blob, filename) {
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  }
}

const ReportPage = ({ history, match }) => {
  const backUrl = "/reports";

  const intl = useIntl();
  const canAccessRetailAttribution = useCanAccessRetailAttribution();

  const reportDefaults = REPORT_TYPES[match.params.report_slug];
  const hiddenColumns = new Set(
    Object.hasOwn(Config.hideReportColumns, match.params.report_slug)
      ? Config.hideReportColumns[match.params.report_slug]
      : [],
  );

  const isNewToBrandReport = reportDefaults.type === REPORT_TYPES["new-to-brand"].type;
  const isOfflineAttributionReport = reportDefaults.type === REPORT_TYPES["omnichannel-attribution"].type;
  const isAudienceReport = reportDefaults.type === REPORT_TYPES.audience.type;
  const isRetailAttributionReport = reportDefaults.type === REPORT_TYPES["retail-attribution"].type;

  const services = React.useRef(
    new Map([
      ["reports", new ReportsService()],
      ["agencies", new AgenciesService()],
    ]),
  );
  const [state, dispatch] = useReducer(reportGridReducer, initialState);

  const shouldDisplayAudienceTitleTooltip = isAudienceReport && checkIfWmx();
  const getInitialReportParams = () => {
    const commonParams = {
      // biome-ignore lint/style/useNamingConvention: <server prop>
      start_date: INITIAL_START_DATE,
      // biome-ignore lint/style/useNamingConvention: <server prop>
      end_date: INITIAL_END_DATE,
      // biome-ignore lint/style/useNamingConvention: <server prop>
      campaign_ids: [],
      // biome-ignore lint/style/useNamingConvention: <server prop>
      advertiser_id: null,
    };

    if (isNewToBrandReport) {
      return {
        ...commonParams,
        // biome-ignore lint/style/useNamingConvention: <server prop>
        lookback_window: 30,
      };
    }

    // for standard reports (performance, geo, app-transparency)
    return {
      ...commonParams,
      // biome-ignore lint/style/useNamingConvention: <server prop>
      reporting_period: "last_30_days",
      // biome-ignore lint/style/useNamingConvention: <server prop>
      time_rollup: "by_day",
    };
  };

  useEffect(() => {
    if (isRetailAttributionReport && !canAccessRetailAttribution) {
      history.push(backUrl);
    }
  }, [history, isRetailAttributionReport, canAccessRetailAttribution]);

  /** Can be "json", "csv", or null, depending on which content type is being loaded. */
  const [showLoaderFor, setShowLoaderFor] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [reportParams, setReportParams] = useState(getInitialReportParams);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <run on mount>
  useEffect(() => {
    return () => {
      state.pager.reset();
    };
  }, []);

  const defaultColumns = Object.hasOwn(Config.defaultReportColumns, match.params.report_slug)
    ? Config.defaultReportColumns[match.params.report_slug].filter((name) => !hiddenColumns.has(name))
    : reportDefaults
      ? reportDefaults.defaultColumns.filter((name) => !hiddenColumns.has(name))
      : [];
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const hasConversionColumns =
    isRetailAttributionReport &&
    selectedColumns.some((columnId) => {
      const col = AVAILABLE_COLUMNS[columnId];
      if (!col) return false;
      const label = intl.formatMessage({
        id: col.messageId,
        defaultMessage: col.name,
      });
      return isConversionColumnLabel(label);
    });
  const hasStrategyColumns =
    isRetailAttributionReport && selectedColumns.some((columnId) => isStrategyColumn(columnId));
  const allowedColumns = reportDefaults
    ? Object.fromEntries(
        reportDefaults.customColumnsOrder
          ? reportDefaults.allowedColumns
              .filter((name) => AVAILABLE_COLUMNS[name] && !hiddenColumns.has(name))
              .map((name) => [name, AVAILABLE_COLUMNS[name]])
          : Object.entries(AVAILABLE_COLUMNS).filter(
              ([name, _]) => reportDefaults.allowedColumns.includes(name) && !hiddenColumns.has(name),
            ),
      )
    : [];

  const getPage = (_e, { activePage }) => {
    dispatch({ type: reportActions.GET_PAGE, data: activePage });
  };

  const setSorting = (column) => {
    dispatch({ type: reportActions.CHANGE_SORT, data: column });
  };

  const createReportModel = (dimensions, metrics, output) => {
    const commonProps = {
      startDate: reportParams.start_date,
      endDate: reportParams.end_date,
      campaignIds: reportParams.campaign_ids,
      dimensions: dimensions,
      metrics: metrics,
      output: output,
      advertiserId: reportParams.advertiser_id,
    };

    if (isNewToBrandReport) {
      return new NewToBrandReportModel({
        ...commonProps,
        lookbackWindow: reportParams.lookback_window,
      });
    }

    //for standard reports (performance, geo, site_transparency, app_transparency)
    return new ReportModel({
      ...commonProps,
      type: reportDefaults.type,
      timeRollup: reportParams.time_rollup,
      reportingPeriod: reportParams.reporting_period,
    });
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <refactoring>
  const runReport = async (output = "json") => {
    const hasMissingDates = !(reportParams.start_date && reportParams.end_date);
    if (reportParams.reporting_period === "custom" && hasMissingDates) {
      return setServerError(
        intl.formatMessage({
          id: "ERROR_EMPTY_REPORT_DATES",
          defaultMessage: "Please select report date range.",
        }),
      );
    }
    if (!reportParams.advertiser_id) {
      return setServerError(
        intl.formatMessage({
          id: "ERROR_EMPTY_ADVERTISER_ID",
          defaultMessage: "Please select an advertiser.",
        }),
      );
    }

    const { dimension: dimensions = [], metric: metrics = [] } = groupBy(
      selectedColumns,
      (columnId) => AVAILABLE_COLUMNS[columnId].kind,
    );
    // Offline Attribution report requires start and end dates in its dimensions
    if (
      !(isOfflineAttributionReport || isAudienceReport || isRetailAttributionReport) &&
      dimensions.includes("start_date")
    ) {
      const index = dimensions.indexOf("start_date");
      if (index > -1) {
        dimensions.splice(index, 1);
      }
    }
    if (
      !(isOfflineAttributionReport || isAudienceReport || isRetailAttributionReport) &&
      dimensions.includes("end_date")
    ) {
      const index = dimensions.indexOf("end_date");
      if (index > -1) {
        dimensions.splice(index, 1);
      }
    }
    if (dimensions.length === 0) {
      return setServerError(
        intl.formatMessage({
          id: "ERROR_EMPTY_DIMENSIONS",
          defaultMessage: "Please select some dimension columns (e. g. Agency Name).",
        }),
      );
    }
    if (metrics.length === 0) {
      return setServerError(
        intl.formatMessage({
          id: "ERROR_EMPTY_METRICS",
          defaultMessage: "Please select some metric columns (e. g. CTR).",
        }),
      );
    }

    // Used for formatting monetary values in other columns:
    if (
      !(isNewToBrandReport || isOfflineAttributionReport || isAudienceReport || isRetailAttributionReport) &&
      output === "json" &&
      !dimensions.includes("campaign_currency_code")
    ) {
      dimensions.push("campaign_currency_code");
    }
    const reportRequest = createReportModel(dimensions, metrics, output);
    setShowLoaderFor(output);
    setServerError(null);
    try {
      const service = services.current.get("reports");
      let reportContent;

      if (output === "json") {
        if (isNewToBrandReport) {
          reportContent = await service.runNewToBrand(reportRequest.toJson());
        } else if (isOfflineAttributionReport) {
          reportContent = await service.runOfflineAttribution(reportRequest.toJson());
        } else if (isAudienceReport) {
          reportContent = await service.runAudience(reportRequest.toJson());
        } else if (isRetailAttributionReport) {
          reportContent = await service.runRetailAttribution(reportRequest.toJson());
        } else {
          reportContent = await service.run(reportRequest.toJson());
        }

        dispatch({ type: reportActions.INIT, data: reportContent.data });
      } else {
        if (isNewToBrandReport) {
          reportContent = await service.runNewToBrandDownload(reportRequest.toJson());
        } else if (isOfflineAttributionReport) {
          reportContent = await service.runOfflineAttributionDownload(reportRequest.toJson());
        } else if (isAudienceReport) {
          reportContent = await service.runAudienceDownload(reportRequest.toJson());
        } else if (isRetailAttributionReport) {
          reportContent = await service.runRetailAttributionDownload(reportRequest.toJson());
        } else {
          reportContent = await service.runDownload(reportRequest.toJson());
        }

        const blob = new Blob([reportContent], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const filename = isNewToBrandReport ? "new_to_brand_report.xlsx" : "report.xlsx";
        downloadBlob(blob, filename);
      }
    } catch (e) {
      setServerError(
        e?.error?.message ||
          intl.formatMessage({
            id: "ERROR_RUNNING_REPORT",
            defaultMessage: "An error occurred while running the report.",
          }),
      );
    } finally {
      setShowLoaderFor(null);
    }
  };

  const handleReportParamsChange = (nextValues) => {
    setReportParams((prevValues) => ({ ...prevValues, ...nextValues }));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    dispatch({ type: reportActions.INIT, data: [] });
  }, [reportParams.advertiser_id, reportParams.campaign_ids, reportParams.lookback_window, reportParams.time_rollup]);

  const handleSelectedColumnsChange = (columns) => {
    setSelectedColumns(columns);
  };

  if (!Object.hasOwn(REPORT_TYPES, match.params.report_slug)) {
    history.push(backUrl, {
      action: "error",
      msg: `Report ${match.params.report_slug} not found`,
    });
    return null;
  }

  return (
    <Segment
      loading={showLoaderFor === "json"}
      basic
      style={{ padding: "0" }}
    >
      <ReportGridContext.Provider
        value={{
          getPage,
          setSorting,
          selectedColumns,
          sortedBy: state.sortedBy,
          sortDirection: state.sortDirection,
          isRetailAttributionReport,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: 1, display: "flex", alignItems: "center", marginBottom: "0.875rem" }}>
            <h1 style={{ marginBottom: "0", marginRight: "0.5rem" }}>
              <NavLink
                to="/reports"
                title={intl.formatMessage({
                  id: "HINT_BACK_REPORTS",
                  defaultMessage: "Back to the Reports",
                })}
                className="black"
              >
                <Icon
                  name="arrow left"
                  size="small"
                />{" "}
                {intl.formatMessage({
                  id: reportDefaults.messageId,
                  defaultMessage: reportDefaults.title,
                })}
              </NavLink>
            </h1>
            {shouldDisplayAudienceTitleTooltip && (
              <Popup
                position="bottom left"
                content={<div>{walmartMXTranslations.tooltipAudienceReportTitleSpanish}</div>}
                inverted
                trigger={
                  <button
                    className="cursor-help"
                    type="button"
                    style={{ background: "none", border: "none", padding: 0, margin: 0 }}
                  >
                    <Icon name="help circle" />
                  </button>
                }
              />
            )}
          </div>
          <div>
            <Button
              positive
              compact
              type="button"
              data-testid="report-run-button"
              onClick={() => runReport()}
            >
              {intl.formatMessage({
                id: "BTN_RUN_REPORT",
                defaultMessage: "Run Report",
              })}
            </Button>
            <Button
              loading={showLoaderFor === "csv"}
              compact
              type="button"
              onClick={() => runReport("csv")}
            >
              {intl.formatMessage({
                id: "BTN_DL_REPORT",
                defaultMessage: "Download Report",
              })}
            </Button>
          </div>
        </div>
        <Divider hidden />
        <Grid className="common_grid">
          <Grid.Row>
            <Grid.Column width={13}>
              {isNewToBrandReport ? (
                <NewToBrandReportForm
                  values={reportParams}
                  updateValues={handleReportParamsChange}
                  onSubmit={runReport}
                  type={reportDefaults.type}
                />
              ) : (
                <ReportParamsForm
                  values={reportParams}
                  updateValues={handleReportParamsChange}
                  onSubmit={runReport}
                  type={reportDefaults.type}
                  hasConversionColumns={hasConversionColumns}
                  hasStrategyColumns={hasStrategyColumns}
                />
              )}
              <Message
                style={{ marginTop: "10px" }}
                error
                hidden={!serverError}
                size="tiny"
                content={serverError}
              />

              <ReportGrid
                items={state.items}
                controls={{ pager: state.pager }}
              />
            </Grid.Column>
            <Grid.Column
              width={3}
              style={{ paddingInlineStart: 0 }}
            >
              <ColumnPicker
                options={allowedColumns}
                defaultOptions={defaultColumns}
                value={selectedColumns}
                onChange={handleSelectedColumnsChange}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </ReportGridContext.Provider>
    </Segment>
  );
};

export default ReportPage;

/**
 * Render grid
 * @param {array} items
 * @param {object} controls
 * @return {jsx}
 * @constructor
 */
export const ReportGrid = ({ items, controls }) => {
  const intl = useIntl();
  const context = useContext(ReportGridContext);
  const shouldShowPagination = controls.pager.total_pages > 1 && context.selectedColumns.length > 0;

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <Table
          className="custom-table"
          data-testid="report-results-table"
          sortable
        >
          <Table.Header>
            <Table.Row>
              {context.selectedColumns.map((columnId) => {
                const headerLabel = intl.formatMessage({
                  id: AVAILABLE_COLUMNS[columnId].messageId,
                  defaultMessage: AVAILABLE_COLUMNS[columnId].name,
                });
                const showAsterisk =
                  context.isRetailAttributionReport &&
                  (isConversionColumnLabel(headerLabel) || isStrategyColumn(columnId));
                return (
                  <Table.HeaderCell
                    key={columnId}
                    textAlign="left"
                    sorted={context.sortedBy === columnId ? context.sortDirection : null}
                    onClick={() => context.setSorting(columnId)}
                  >
                    {headerLabel}
                    {showAsterisk && (
                      <span
                        style={{ color: "red", marginLeft: "2px" }}
                        aria-hidden="true"
                      >
                        *
                      </span>
                    )}
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items === null ? (
              <ReportGridEmptyRow>
                {intl.formatMessage({
                  id: "EMPTY_REPORT_PARAMS",
                  defaultMessage: "Select parameters to generate the report",
                })}
              </ReportGridEmptyRow>
            ) : items.length ? (
              items.map((item) => {
                return (
                  <ReportGridItem
                    key={uuid()}
                    {...item}
                  />
                );
              })
            ) : (
              <ReportGridEmptyRow>
                {intl.formatMessage({
                  id: "EMPTY_REPORT",
                  defaultMessage: "Current report contains no data",
                })}
              </ReportGridEmptyRow>
            )}
          </Table.Body>
        </Table>
      </div>

      {shouldShowPagination && (
        <div style={{ textAlign: "right", marginTop: "1em" }}>
          <Pagination
            activePage={controls.pager.page}
            firstItem={null}
            lastItem={null}
            onPageChange={context.getPage}
            size="mini"
            totalPages={controls.pager.total_pages}
          />
        </div>
      )}
    </>
  );
};

/**
 * @param {*} props.children - empty state message
 */
const ReportGridEmptyRow = ({ children }) => (
  <Table.Row>
    <Table.Cell
      colSpan="5000"
      textAlign="center"
      style={{ paddingLeft: 0, paddingRight: 0 }}
    >
      <div
        style={{
          // Width of a <Grid.Column width={13} /> minus all the paddings:
          width: "calc(81.25vw - 2rem - .78571429em)",
          position: "absolute",
        }}
      >
        {children}
      </div>
      {/* A single whitespace to move scrollbar further below: */}
      &nbsp;
    </Table.Cell>
  </Table.Row>
);
ReportGridEmptyRow.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Generate grid item
 * @param {object} item
 * @return {*}
 * @constructor
 */
const ReportGridItem = (item) => {
  const context = useContext(ReportGridContext);

  return (
    <Table.Row>
      {context.selectedColumns.map((columnId) => (
        <Table.Cell key={columnId}>
          {Object.hasOwn(item, columnId) ? (
            <ReportGridValue
              type={AVAILABLE_COLUMNS[columnId].type}
              value={item[columnId]}
              row={item}
            />
          ) : (
            ""
          )}
        </Table.Cell>
      ))}
    </Table.Row>
  );
};

const FORMATTERS = {
  // Should IDs be linked with names perhaps?
  // E. g. advertiser_id + advertiser_name -> one Advertiser column
  id: (val) => val,
  string: (val) => val,
  "money-usd": (val, _row) => formatMoney(val, "USD", "en-US", 2),

  money: (val, row) => formatMoney(val, row.campaign_currency_code || Config.defaultCurrency, Config.defaultLocale, 2),

  datetime: (val) => (val === "NULL" || val == null ? "NULL" : lightFormatDate(parseISO(val), Config.dateTimeFormat)),
  count: (val) => formatNumber(val, Config.defaultLocale),
  count_whole: (val) => formatNumber(Math.floor(val), Config.defaultLocale),

  // how many decimal places?
  percent: (val) => `${formatNumber(val, Config.defaultLocale, 2)}%`,
  ratio: (val) => formatNumber(val, Config.defaultLocale, 2),
  float: (val) => formatNumber(val, Config.defaultLocale, 2),
};

/**
 * Display report cell value depending on its' type.
 * @param {string} props.type - type of the cell
 * @param {string|null} props.value - value of the cell, if it is loaded
 * @param {object} props.row - current report row
 */
const ReportGridValue = ({ type, value = null, row }) => {
  const formatter = FORMATTERS[type];
  if (!formatter) {
    console.error("[ReportGridValue] No formatter available for", type);
  }
  return formatter ? formatter(value, row) : `#WAT?${type}`;
};
